// src/pages/subscription/components/PaymentHistory.jsx
import React, { useState, useEffect } from 'react';
import { Calendar, CreditCard, FileText, Download, Receipt, Loader, Eye, X, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { useAlert } from '../../../contexts/AlertContext';
import { useAuth } from '../../../hooks/useAuth';
import { DataTable } from '../../../components/common';
import { getPaymentHistory, getNextInvoice } from '../../../services/api/subscription';
import { formatPrice } from '../../../utils/subscriptionUtils';
import { formatDate, formatDateTime } from '../../../utils/dateUtils';
import { generatePaymentInvoice } from '../../../utils/invoiceGenerator';
import { getImageUrl, handleImageError } from '../../../utils/imageUtils';
import styles from './PaymentHistory.module.css';

const PaymentHistory = () => {
  const { t } = useLocalization();
  const { error: showError } = useAlert();
  const { user } = useAuth();

  const [payments, setPayments] = useState([]);
  const [nextInvoice, setNextInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInvoice, setIsLoadingInvoice] = useState(true);
  const [selectedProof, setSelectedProof] = useState(null);
  const [showProofModal, setShowProofModal] = useState(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    pageSizeOptions: [10, 20, 50]
  });

  const [sorting, setSorting] = useState({
    orderBy: 'paymentDate',
    orderDirection: 'DESC'
  });

  const currency = user?.business?.currency || 'LKR';

  useEffect(() => {
    loadPaymentHistory();
    loadNextInvoice();
  }, [pagination.currentPage, pagination.pageSize]);

  const loadPaymentHistory = async () => {
    try {
      setIsLoading(true);
      const response = await getPaymentHistory(
        pagination.currentPage,
        pagination.pageSize
      );

      setPayments(response.payments || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0
      }));
    } catch (err) {
      console.error('Error loading payment history:', err);
      if (err.response?.status !== 404) {
        showError(t('subscription.loadPaymentsFailed'), {
          title: t('common.error')
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const loadNextInvoice = async () => {
    try {
      setIsLoadingInvoice(true);
      const response = await getNextInvoice();
      setNextInvoice(response.invoice);
    } catch (err) {
      console.error('Error loading next invoice:', err);
      // Don't show error for 404, just means no upcoming invoice
    } finally {
      setIsLoadingInvoice(false);
    }
  };

  const formatPaymentMethod = (paymentType) => {
    if (!paymentType) return 'N/A';
    
    // Convert snake_case to Title Case
    return paymentType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getBankTransferStatus = (payment) => {
    if (payment.approvedAt) {
      return {
        status: 'Approved',
        color: '#10b981',
        bg: '#10b98120',
        text: '#065f46',
        icon: CheckCircle
      };
    } else if (payment.rejectedAt) {
      return {
        status: 'Rejected',
        color: '#ef4444',
        bg: '#ef444420',
        text: '#991b1b',
        icon: XCircle
      };
    } else {
      return {
        status: 'Pending',
        color: '#f59e0b',
        bg: '#f59e0b20',
        text: '#92400e',
        icon: Clock
      };
    }
  };

  const getPaymentStatus = (payment) => {
    if (payment.paymentType === 'bank_transfer') {
      return getBankTransferStatus(payment);
    }

    // For card payments, use the status field
    const statusMap = {
      success: { status: 'Paid', color: '#10b981', bg: '#10b98120', text: '#065f46', icon: CheckCircle },
      pending: { status: 'Pending', color: '#f59e0b', bg: '#f59e0b20', text: '#92400e', icon: Clock },
      failed: { status: 'Failed', color: '#ef4444', bg: '#ef444420', text: '#991b1b', icon: XCircle }
    };

    return statusMap[payment.status] || statusMap.pending;
  };

  const getPaymentStatusBadge = (payment) => {
    const statusInfo = getPaymentStatus(payment);
    const Icon = statusInfo.icon;

    return (
      <div 
        className={styles.statusBadge}
        style={{
          backgroundColor: statusInfo.bg,
          borderColor: statusInfo.color,
          color: statusInfo.text
        }}
      >
        <Icon size={14} />
        {statusInfo.status}
      </div>
    );
  };

  const handleViewProof = (proofUrl) => {
    setSelectedProof(proofUrl);
    setShowProofModal(true);
  };

  const handleDownloadInvoice = async (payment) => {
    try {
      const invoiceData = {
        invoiceNumber: payment.transactionId,
        date: payment.paymentDate,
        businessName: user?.business?.name || 'N/A',
        planName: payment.metadata?.planName || 'N/A',
        amount: payment.amount,
        currency: payment.currency,
        paymentMethod: formatPaymentMethod(payment.paymentType),
        status: getPaymentStatus(payment).status
      };

      await generatePaymentInvoice(invoiceData);
    } catch (err) {
      console.error('Error generating invoice:', err);
      showError('Failed to generate invoice', {
        title: t('common.error')
      });
    }
  };

  const canDownloadInvoice = (payment) => {
    if (payment.paymentType === 'card_payment') {
      return payment.status === 'success';
    } else if (payment.paymentType === 'bank_transfer') {
      return payment.approvedAt !== null;
    }
    return false;
  };

  const columns = [
    {
      key: 'paymentDate',
      header: t('subscription.paymentDate'),
      sortable: true,
      width: '120px',
      render: (payment) => (
        <div className={styles.dateCell}>
          <Calendar size={14} />
          {formatDate(payment.paymentDate)}
        </div>
      )
    },
    {
      key: 'amount',
      header: t('subscription.amount'),
      sortable: true,
      width: '120px',
      align: 'right',
      render: (payment) => (
        <div className={styles.amountCell}>
          {formatPrice(payment.amount, currency)}
        </div>
      )
    },
    {
      key: 'paymentMethod',
      header: t('subscription.paymentMethod'),
      width: '140px',
      render: (payment) => (
        <div className={styles.methodCell}>
          <CreditCard size={14} />
          {formatPaymentMethod(payment.paymentType)}
        </div>
      )
    },
    {
      key: 'transactionId',
      header: t('subscription.transactionId'),
      width: '180px',
      render: (payment) => (
        <div className={styles.transactionCell}>
          {payment.transactionId || 'N/A'}
        </div>
      )
    },
    {
      key: 'status',
      header: t('subscription.status'),
      width: '140px',
      render: (payment) => (
        <div>
          {getPaymentStatusBadge(payment)}
          
          {/* Show approval/rejection details for bank transfers */}
          {payment.paymentType === 'bank_transfer' && (
            <div className={styles.statusDetails}>
              {payment.approvedAt && (
                <div className={styles.statusInfo}>
                  <CheckCircle size={12} />
                  <span>{formatDateTime(payment.approvedAt)}</span>
                </div>
              )}
              {payment.rejectedAt && (
                <>
                  <div className={styles.statusInfo}>
                    <XCircle size={12} />
                    <span>{formatDateTime(payment.rejectedAt)}</span>
                  </div>
                  {payment.rejectionReason && (
                    <div className={styles.rejectionReason}>
                      {payment.rejectionReason}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'proof',
      header: t('subscription.payment.paymentProof'),
      width: '40px',
      align: 'center',
      render: (payment) => (
        payment.paymentProof ? (
          <button
            className={styles.proofButton}
            onClick={(e) => {
              e.stopPropagation();
              handleViewProof(payment.paymentProof);
            }}
          >
            <Eye size={16} />
            {/* {t('subscription.payment.viewProof')} */}
          </button>
        ) : (
          <span className={styles.naText}>-</span>
        )
      )
    },
    {
      key: 'invoice',
      header: t('subscription.invoice'),
      width: '40px',
      align: 'center',
      render: (payment) => (
        canDownloadInvoice(payment) ? (
          <button
            className={styles.invoiceButton}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadInvoice(payment);
            }}
          >
            <Download size={16} />
            {/* {t('common.download')} */}
          </button>
        ) : (
          <span className={styles.naText}>-</span>
        )
      )
    }
  ];

  return (
    <div className={styles.paymentHistory}>
      {/* Next Invoice Preview */}
      {nextInvoice && (
        <div className={styles.nextInvoiceCard}>
          <div className={styles.cardHeader}>
            <div className={styles.headerTitle}>
              <Receipt size={20} />
              <h3>{t('subscription.nextInvoice')}</h3>
            </div>
            {isLoadingInvoice && <Loader className={styles.spinner} size={18} />}
          </div>

          {!isLoadingInvoice && (
            <div className={styles.cardBody}>
              <div className={styles.invoiceGrid}>
                <div className={styles.invoiceItem}>
                  <div className={styles.invoiceLabel}>{t('subscription.planName')}</div>
                  <div className={styles.invoiceValue}>{nextInvoice.planName}</div>
                </div>

                <div className={styles.invoiceItem}>
                  <div className={styles.invoiceLabel}>{t('subscription.billingCycle')}</div>
                  <div className={styles.invoiceValue}>
                    {t(`subscription.${nextInvoice.billingCycle}`)}
                  </div>
                </div>

                <div className={styles.invoiceItem}>
                  <div className={styles.invoiceLabel}>{t('subscription.nextBillingDate')}</div>
                  <div className={styles.invoiceValue}>
                    {formatDate(nextInvoice.nextBillingDate)}
                  </div>
                </div>

                <div className={styles.invoiceItem}>
                  <div className={styles.invoiceLabel}>{t('subscription.amount')}</div>
                  <div className={styles.invoiceValueHighlight}>
                    {formatPrice(nextInvoice.total, currency)}
                  </div>
                </div>
              </div>

              {nextInvoice.items && nextInvoice.items.length > 0 && (
                <div className={styles.invoiceItems}>
                  <div className={styles.itemsHeader}>{t('subscription.lineItems')}</div>
                  {nextInvoice.items.map((item, index) => (
                    <div key={index} className={styles.lineItem}>
                      <span className={styles.lineItemDescription}>{item.description}</span>
                      <span className={styles.lineItemAmount}>
                        {formatPrice(item.amount, currency)}
                      </span>
                    </div>
                  ))}

                  <div className={styles.invoiceTotals}>
                    <div className={styles.totalRow}>
                      <span>{t('subscription.subtotal')}</span>
                      <span>{formatPrice(nextInvoice.subtotal, currency)}</span>
                    </div>
                    {nextInvoice.tax > 0 && (
                      <div className={styles.totalRow}>
                        <span>{t('subscription.tax')}</span>
                        <span>{formatPrice(nextInvoice.tax, currency)}</span>
                      </div>
                    )}
                    <div className={styles.totalRowFinal}>
                      <span>{t('subscription.total')}</span>
                      <span>{formatPrice(nextInvoice.total, currency)}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Payment History Table */}
      <div className={styles.tableCard}>
        <div className={styles.tableHeader}>
          <div className={styles.headerTitle}>
            <FileText size={20} />
            <h3>{t('subscription.paymentHistory')}</h3>
          </div>
        </div>

        <DataTable
          data={payments}
          columns={columns}
          sorting={sorting}
          onSortChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          loading={isLoading}
          emptyMessage={t('subscription.noPaymentsYet')}
          emptyDescription={t('subscription.noPaymentsDescription')}
          emptyIcon={<CreditCard size={64} />}
        />
      </div>

      {/* Payment Proof Modal */}
      {showProofModal && selectedProof && (
        <div className={styles.modalOverlay} onClick={() => setShowProofModal(false)}>
          <div className={styles.proofModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{t('subscription.payment.paymentProof')}</h3>
              <button 
                className={styles.closeButton}
                onClick={() => setShowProofModal(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <img 
                src={getImageUrl(selectedProof)}
                alt="Payment Proof"
                className={styles.proofImage}
                onError={(e) => handleImageError(e)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentHistory;